import type { ReactNode } from 'react';

import { Box, chakra, VisuallyHidden } from '@chakra-ui/react';
import { Drawer } from 'vaul';

import { BottomSheetHeader } from './bottom-sheet-header';

export type BottomSheetSize = 'default' | 'full';

export interface BottomSheetProps {
  children?: ReactNode;
  dismissible?: boolean;
  /** Removes padding from the bottom sheet. */
  flush?: boolean;
  footer?: ReactNode;
  header?: ReactNode;
  modal?: boolean;
  onBack?: () => void;
  onDismiss?: () => void;
  onOpenChange?: (open: boolean) => void;
  open: boolean;
  showBackButton?: boolean;
  showCloseButton?: boolean;
  showHandle?: boolean;
  /** `full` fills the viewport height (100dvh). `default` sizes to content with a max of one viewport. */
  size?: BottomSheetSize;
  subtitle?: ReactNode;
  title?: ReactNode;
}

const DrawerOverlay = chakra(Drawer.Overlay, {
  base: {
    bg: 'blackAlpha.600',
    inset: 0,
    position: 'fixed',
    // zIndex needs to match Drawer Content for nested modals to work
    zIndex: 'modal',
  },
});

const DrawerContent = chakra(Drawer.Content, {
  base: {
    bg: 'fill.background.default',
    borderTopRadius: '2xl',
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    left: 0,
    minH: 0,
    outline: 'none',
    overflow: 'hidden',
    position: 'fixed',
    right: 0,
    width: '100%',
    zIndex: 'modal',
  },
});

export const BottomSheet = ({
  children,
  dismissible = true,
  footer,
  header,
  modal,
  onBack,
  onDismiss,
  onOpenChange,
  open,
  showBackButton = false,
  showCloseButton = false,
  showHandle = false,
  size = 'default',
  flush = false,
  subtitle,
  title,
}: BottomSheetProps) => {
  const isFullHeight = size === 'full';
  const padding = flush ? 0 : 4;
  const hasTextHeader = Boolean(title || subtitle);
  const hasContentHeader = Boolean(
    header || hasTextHeader || showCloseButton || showBackButton || showHandle,
  );
  const handleClose = () => {
    // The header X is a plain click handler, not a Vaul dismiss. Root's
    // onOpenChange only fires for swipe/overlay, so the X must invoke both.
    onOpenChange?.(false);
    onDismiss?.();
  };
  const closeOnlyHeader = flush && !hasContentHeader && (!!onDismiss || !!onOpenChange);
  const shouldCenterTextHeader =
    isFullHeight && Boolean(title) && !subtitle && !header && !showBackButton;
  const hasHeader = hasContentHeader || closeOnlyHeader;

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange?.(nextOpen);
        if (!nextOpen) {
          onDismiss?.();
        }
      }}
      modal={modal}
      dismissible={dismissible}
      direction="bottom"
    >
      <Drawer.Portal>
        {/* Block interactions while open; release on close so vaul's doc-level tap-to-dismiss listener and page elements aren't blocked during the exit animation */}
        <DrawerOverlay style={{ pointerEvents: open ? 'auto' : 'none' }} />
        <DrawerContent
          h={isFullHeight ? '100dvh' : undefined}
          maxH={isFullHeight ? '100dvh' : '100vh'}
        >
          {/* Radix dialog requires a title; keep a fallback when callers omit one. */}
          {!title && (
            <VisuallyHidden asChild>
              <Drawer.Title>Bottom sheet</Drawer.Title>
            </VisuallyHidden>
          )}
          <VisuallyHidden asChild>
            <Drawer.Description>Bottom sheet content</Drawer.Description>
          </VisuallyHidden>
          {hasHeader && (
            <BottomSheetHeader
              flush={flush}
              showBackButton={showBackButton}
              showCloseButton={showCloseButton}
              showHandle={showHandle}
              subtitle={subtitle}
              title={title}
              titleAlign={shouldCenterTextHeader ? 'center' : 'left'}
              onBack={onBack}
              onClose={handleClose}
            >
              {header}
            </BottomSheetHeader>
          )}
          <Box
            display="flex"
            flexDirection="column"
            flex="1"
            gap={2}
            minH={0}
            px={padding}
            pb={padding}
            pt={hasHeader ? 2 : padding}
          >
            <Box overflowY="auto" flex="1" minH={0}>
              {children}
            </Box>
            {footer && <Box>{footer}</Box>}
          </Box>
        </DrawerContent>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

export default BottomSheet;
